import { ApolloProvider } from '@apollo/client';
import { AuthenticatedSignalRContextProvider, AuthProvider, AuthProviderConfig } from '@cmg/auth';
import { ErrorBoundary, GlobalStyle, ThemeProvider } from '@cmg/common';
import {
  AppLayout as DesignSystemAppLayout,
  DesignSystemProvider,
  DesignSystemProviderProps,
} from '@cmg/design-system';
import React from 'react';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import graphqlApiClient from '../../common/api/graphqlApiClient';
import { AppSettingsContext } from '../../common/config';
import { getAppSettings } from '../../common/config/appSettings';
import { ContactSupportModalContextProvider } from '../../common/context/ContactSupportModalContext';
import { XcI18nProvider } from '../../common/providers/XcI18nProvider';
import { XcMixpanelAppContextProvider } from '../../common/providers/XcMixpanelAppContextProvider';
import { PushNotificationProvider } from '../../common/push-notification';
import store from '../../common/redux/store';
import routeFactory from '../../common/util/routeFactory';
import { AppBar } from '../../design-system/layout/app-bar';
import { ReactRouterMUILink } from '../../design-system/react-router/ReactRouterMUILink';
import RootRouter from './RootRouter';

const appSettings = getAppSettings();
const NormalizedHeightRootCss = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }
`;

const designSystemProviderProps: DesignSystemProviderProps = {
  themeConfig: { LinkComponent: ReactRouterMUILink },
};

const authConfig: AuthProviderConfig = {
  env: {
    rootDomain: appSettings.env.rootDomain,
    secureCookies: appSettings.env.secureCookies,
  },
  client: {
    basename: appSettings.client.basename,
  },
  logging: {
    appName: 'Xecution',
    mixPanelToken: appSettings.logging.mixPanelToken,
  },
  auth: {
    clientId: appSettings.auth.clientId,
    oidcAuthorityBaseUrl: appSettings.auth.oidcAuthorityBaseUrl,
    tokenRenewalInterval: appSettings.auth.tokenRenewalIntervalInMinutes,
    scopes: Object.values(appSettings.auth.scopes),
  },
  actions: {
    onLogout: () => {
      window.location.href = routeFactory.loggedOut.getUrlPath();
    },
  },
};

const router = createBrowserRouter(
  [
    {
      path: '*',
      element: (
        <ErrorBoundary>
          <ContactSupportModalContextProvider>
            <DesignSystemAppLayout
              header={
                <PushNotificationProvider>
                  <AppBar />
                </PushNotificationProvider>
              }
              page={<RootRouter />}
            />
          </ContactSupportModalContextProvider>
        </ErrorBoundary>
      ),
    },
  ],
  {
    basename: appSettings.client.basename,
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

const App: React.FC = () => {
  return (
    <AuthProvider authConfig={authConfig}>
      <XcI18nProvider>
        <AppSettingsContext.Provider value={appSettings}>
          <Provider store={store}>
            <ApolloProvider client={graphqlApiClient}>
              <XcMixpanelAppContextProvider>
                <AuthenticatedSignalRContextProvider>
                  <GlobalStyle />
                  <NormalizedHeightRootCss />
                  <DesignSystemProvider {...designSystemProviderProps}>
                    <ThemeProvider>
                      <RouterProvider router={router} future={{ v7_startTransition: true }} />
                    </ThemeProvider>
                  </DesignSystemProvider>
                </AuthenticatedSignalRContextProvider>
              </XcMixpanelAppContextProvider>
            </ApolloProvider>
          </Provider>
        </AppSettingsContext.Provider>
      </XcI18nProvider>
    </AuthProvider>
  );
};

export default App;
